import { computed, inject, Injectable, signal, Signal, WritableSignal } from "@angular/core";
import { catchError, EMPTY, map, MonoTypeOperatorFunction, Observable, of, scan, shareReplay, startWith, Subject, switchMap, tap } from "rxjs";
import { HttpClient, HttpParams, HttpResponse } from "@angular/common/http";
import { toObservable, toSignal } from "@angular/core/rxjs-interop";
import { TableConfig } from "@shared/components/table-implementation/types/table-config";
import { buildTableApiParams } from "@shared/components/table-implementation/functions/table-api-builder";
import { ApiCollectionResponse } from "@shared/types/api/api-collection-response";
import { TABLE_DATA_API_CONFIG_TYPES, TableDataApiConfig, TableDataApiOptions } from "./types/table-api-config";
import { TABLE_DATA_CONFIG } from "./tokens/table-data-config.token";
import { overrideMerge } from "@shared/components/table-implementation/functions/merge-http-params";
import { TableConfigService } from "./table-config.service";

export type TableDataFetch<T> = (params: HttpParams) => Observable<HttpResponse<ApiCollectionResponse<T>> | undefined>;
export interface FetchRequest {
  options?: TableDataApiOptions;
  page?: number;
}

interface PageChunk<T> {
  page: number;
  items: T[];
}

interface PagedData<T> {
  pages: Map<number, T[]>;
  merged: T[];
}

export interface EntityState<T> {
  entities: T[];
  currentPage: number;
  hasMore: boolean;
}
@Injectable()
export class TableDataService<T> {
  private _providedConfig: TableDataApiConfig<T> | null = inject(TABLE_DATA_CONFIG, { host: true, optional: true });

  // eslint-disable-next-line @unicorn/no-useless-undefined
  private _configOverride: WritableSignal<TableDataApiConfig<T> | undefined> = signal(undefined);

  // eslint-disable-next-line @unicorn/consistent-function-scoping
  private _config: Signal<TableDataApiConfig<T> | null> = computed(() => this._configOverride() ?? this._providedConfig);

  private _config$: Observable<TableDataApiConfig<T> | null> = toObservable(this._config);

  private _httpClient: HttpClient = inject(HttpClient);

  private _tableConfigService: TableConfigService = inject(TableConfigService);

  private _selectedTableConfig: Signal<TableConfig | undefined> = toSignal(this._tableConfigService.selectedConfig$);

  private _fetchRequested$: Subject<FetchRequest | undefined> = new Subject<FetchRequest | undefined>();

  private _isAllDataFetched: boolean = false;

  private _currentPage: number = 0;

  /**
   * Data-Observable emits every time the selectedTable-config changes or when reloaded manually via {@link reload | reload-method}.
   *
   * When loadImagesLazy from the {@link TABLE_DATA_CONFIG | TABLE_DATA_CONFIG}-options is set to true, it first emits the data without images and then again with images.
   */
  public data$: Observable<EntityState<T>> = this._tableConfigService.selectedConfig$.pipe(
    tap(() => {
      // Todo remove side-effect
      this._isAllDataFetched = false;
      this._currentPage = 0;
    }),
    switchMap(() => this._config$.pipe(
      switchMap(() => this._fetchRequested$.pipe(
        startWith(void 0),
        tap((fetchRequest: FetchRequest | undefined) => {
          if (fetchRequest?.page === 0) {
            this._isAllDataFetched = false;
            this._currentPage = 0;
          }
          // Todo remove side-effect
        }),
        switchMap((fetchRequest: FetchRequest | undefined) => {
          const page: number = fetchRequest?.page ?? 0;

          return this.fetchData(fetchRequest).pipe(
            this.validateResponse(page),
            map((resp: HttpResponse<ApiCollectionResponse<T>> | undefined): PageChunk<T> => ({ page, items: resp?.body?.data ?? [] })),
            catchError(() => EMPTY)
          );
        }),
        // For the pages to be appended instead of replaced. If a page gets emitted a second time the data of the previous emit of the page gets overwritten.
        scan((state: PagedData<T>, chunk: PageChunk<T>) => {
          const isFirstPage: boolean = chunk.page === 0;
          const pages: Map<number, T[]> = isFirstPage ? new Map<number, T[]>() : new Map<number, T[]>(state.pages);

          pages.set(chunk.page, chunk.items);

          const max: number = Math.max(...pages.keys(), 0);
          const merged: T[] = [];

          for (let i: number = 0; i <= max; i++) {
            const part: T[] | undefined = pages.get(i);

            if (part) merged.push(...part);
          }

          return { pages, merged };
        }, { pages: new Map<number, T[]>(), merged: [] as T[] }),
        map((value: PagedData<T>): EntityState<T> => ({ entities: value.merged, currentPage: this._currentPage, hasMore: !this._isAllDataFetched }))
      ))
    )),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  private _reset$: Subject<void> = new Subject<void>();

  public reset$: Observable<void> = this._reset$.asObservable()
    .pipe(
      tap(() => {
        this._isAllDataFetched = false;
        this._currentPage = 0;
      })
    );


  /**
   * Sets the {@link TableDataApiConfig | config} of the service to cover options that are only available at injection-context.
   * If a config was provided before the new one will be taken as the config of the service
   * @param config - the config of the service
   */
  public setConfig (config: TableDataApiConfig<T>): void {
    this._configOverride.set(config);
  }

  private validateResponse (page: number): MonoTypeOperatorFunction<HttpResponse<ApiCollectionResponse<T>> | undefined> {
    return tap({
      next: (resp: HttpResponse<ApiCollectionResponse<T>> | undefined) => {
        if (resp?.status === 206)
          this._currentPage = page + 1;
        else if (resp?.status === 200)
          this._isAllDataFetched = true;
      }
    });
  }


  public reload (): void {
    this._fetchRequested$.next({ page: 0 });
  }

  public nextPage (): void {
    this._fetchRequested$.next({ page: this._currentPage });
  }

  public fetchData (fetchRequest?: FetchRequest): Observable<HttpResponse<ApiCollectionResponse<T>> | undefined> {
    if (!this._selectedTableConfig() || this._isAllDataFetched || !this._config()) return of();

    // eslint-disable-next-line @tseslint/no-non-null-assertion
    const config: TableDataApiConfig<T> = fetchRequest?.options ? { ...this._config()!, options: fetchRequest.options } : { ...this._config()! };
    const nextPage: number = fetchRequest?.page ?? 0;
    const pageSize: number = config.options?.pageSize ?? 30;
    /** Append custom params to the default ones */
    const makeParams = (fieldsToSkip: string[] = []): HttpParams => {
      let params: HttpParams = config.options?.httpParams ?? new HttpParams();

      const additionalParams: HttpParams = buildTableApiParams(
        // eslint-disable-next-line @tseslint/no-non-null-assertion
        this._selectedTableConfig()!,
        pageSize,
        nextPage * pageSize,
        {
          fieldsToSkip: [ ...fieldsToSkip, ...config.options?.fieldsToSkip ?? [] ],
          ignoreFilter: Boolean(config.options?.ignoreFilter)
        }
      );

      params = overrideMerge(additionalParams, params);

      return params;
    };

    return config.type === TABLE_DATA_API_CONFIG_TYPES.custom
      ? config.fetch(makeParams())
      : this._httpClient.get<ApiCollectionResponse<T>>(
        config.url,
        { params: makeParams(), observe: "response" }
      );
  }
}
